import React, { useState } from 'react';
import { useCoffee } from '../context/CoffeeContext';
import { ScatterChart, Scatter, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Rectangle, Legend } from 'recharts';
export const BrewingControlChart: React.FC = () => {
  const {
    coffeeEntries,
    coffeeBags
  } = useCoffee();
  const [selectedBagId, setSelectedBagId] = useState<string>('all');
  const getBagName = (bagId: string) => {
    const bag = coffeeBags.find(b => b.id === bagId);
    return bag ? bag.name : 'Café Desconocido';
  };
  // Filter entries based on selected bag
  const filteredEntries = selectedBagId === 'all' ? coffeeEntries : coffeeEntries.filter(entry => entry.bagId === selectedBagId);
  // Prepare data for the chart
  const chartData = filteredEntries.map(entry => ({
    id: entry.id,
    name: getBagName(entry.bagId),
    method: entry.brewMethod,
    tds: entry.tds,
    extraction: entry.extractionYield,
    date: new Date(entry.date).toLocaleDateString()
  }));
  // Custom shape for the scatter plot points
  const CustomShape = (props: any) => {
    const {
      cx,
      cy,
      fill
    } = props;
    return <Rectangle x={cx - 4} y={cy - 4} width={8} height={8} fill={fill} angle={45} data-id="element-150" />;
  };
  // Custom tooltip for the chart
  const CustomTooltip = ({
    active,
    payload
  }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return <div className="bg-white p-2 border border-amber-300 shadow-md rounded" data-id="element-151">
          <p className="font-medium" data-id="element-152">{data.name}</p>
          <p className="text-sm" data-id="element-153">
            {data.method} • {data.date}
          </p>
          <p className="text-sm" data-id="element-154">TDS: {data.tds}%</p>
          <p className="text-sm" data-id="element-155">Extracción: {data.extraction.toFixed(2)}%</p>
        </div>;
    }
    return null;
  };
  return <div className="max-w-md mx-auto" data-id="element-156">
      <h2 className="text-xl font-bold mb-4 text-amber-900" data-id="element-157">
        Coffee Brewing Control Chart
      </h2>
      <div className="mb-4" data-id="element-158">
        <label className="block text-sm font-medium text-amber-700 mb-1" data-id="element-159">
          Filtrar por Café
        </label>
        <select value={selectedBagId} onChange={e => setSelectedBagId(e.target.value)} className="w-full px-3 py-2 border border-amber-300 rounded-md" data-id="element-160">
          <option value="all" data-id="element-161">Todos los cafés</option>
          {coffeeBags.map(bag => <option key={bag.id} value={bag.id} data-id="element-162">
              {bag.name}
            </option>)}
        </select>
      </div>
      {chartData.length > 0 ? <div className="bg-white p-4 rounded-lg shadow" data-id="element-163">
          <div className="h-80" data-id="element-164">
            <ResponsiveContainer width="100%" height="100%" data-id="element-165">
              <ScatterChart margin={{
            top: 30,
            right: 20,
            bottom: 20,
            left: 20
          }} data-id="element-166">
                <CartesianGrid strokeDasharray="3 3" data-id="element-167" />
                <XAxis type="number" dataKey="extraction" name="Extracción" domain={[16, 24]} label={{
              value: 'Extracción (%)',
              position: 'bottom',
              style: {
                fontSize: '0.8rem'
              }
            }} tick={{
              fontSize: 12
            }} data-id="element-168" />
                <YAxis type="number" dataKey="tds" name="TDS" domain={[1, 1.8]} label={{
              value: 'TDS (%)',
              angle: -90,
              position: 'left',
              style: {
                fontSize: '0.8rem'
              }
            }} tick={{
              fontSize: 12
            }} data-id="element-169" />
                <Tooltip content={<CustomTooltip data-id="element-171" />} data-id="element-170" />
                <Legend verticalAlign="top" height={36} wrapperStyle={{
              paddingBottom: '10px',
              fontSize: '12px'
            }} data-id="element-172" />
                {/* Ideal extraction zone */}
                <Scatter name="Zona Ideal" data={[{
              extraction: 18,
              tds: 1.15
            }, {
              extraction: 18,
              tds: 1.45
            }, {
              extraction: 22,
              tds: 1.45
            }, {
              extraction: 22,
              tds: 1.15
            }]} fill="rgba(245, 158, 11, 0.2)" line shape={<CustomShape data-id="element-174" />} data-id="element-173" />
                {/* Actual brew data */}
                <Scatter name="Preparaciones" data={chartData} fill="#d97706" data-id="element-175" />
              </ScatterChart>
            </ResponsiveContainer>
          </div>
          <div className="mt-4 text-sm" data-id="element-176">
            <p className="font-medium text-amber-800" data-id="element-177">Interpretación:</p>
            <ul className="list-disc pl-5 text-gray-700 space-y-1 mt-1" data-id="element-178">
              <li data-id="element-179">Región central: Equilibrado (18-22% extracción)</li>
              <li data-id="element-180">Izquierda: Sub-extraído (ácido, poco cuerpo)</li>
              <li data-id="element-181">Derecha: Sobre-extraído (amargo, astringente)</li>
              <li data-id="element-182">Arriba: Fuerte (alta concentración)</li>
              <li data-id="element-183">Abajo: Débil (baja concentración)</li>
            </ul>
          </div>
        </div> : <div className="bg-white p-4 rounded-lg shadow text-center" data-id="element-184">
          <p className="text-amber-800" data-id="element-185">
            No hay datos suficientes para mostrar el gráfico.
          </p>
          <p className="text-sm text-gray-600 mt-1" data-id="element-186">
            Registra preparaciones con datos de TDS para visualizarlas aquí.
          </p>
        </div>}
    </div>;
};